import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const STORAGE_KEY = "vd_cookie_consent";

export const getCookieConsent = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
};

export const openCookiePreferences = () => {
  window.dispatchEvent(new Event("open-cookie-preferences"));
};

export const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (!getCookieConsent()) setVisible(true);
    const handler = () => {
      const c = getCookieConsent();
      setAnalytics(c?.analytics || false);
      setMarketing(c?.marketing || false);
      setShowPrefs(true);
    };
    window.addEventListener("open-cookie-preferences", handler);
    return () => window.removeEventListener("open-cookie-preferences", handler);
  }, []);

  const save = (consent) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...consent, ts: Date.now() }));
    setVisible(false);
    setShowPrefs(false);
  };

  const acceptAll = () => save({ necessary: true, analytics: true, marketing: true });
  const rejectAll = () => save({ necessary: true, analytics: false, marketing: false });
  const savePrefs = () => save({ necessary: true, analytics, marketing });

  if (!visible && !showPrefs) return null;

  return (
    <>
      {visible && !showPrefs && (
        <div
          data-testid="cookie-banner"
          className="fixed bottom-0 left-0 right-0 z-[60] bg-[#0A0A0A] text-white border-t border-white/10"
        >
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex flex-col lg:flex-row lg:items-center gap-5">
            <div className="flex-1">
              <p className="font-serif text-lg mb-1">Rispettiamo la tua privacy</p>
              <p className="text-sm text-white/60 font-light leading-relaxed text-left">
                Utilizziamo cookie tecnici necessari e, previo consenso, cookie analitici e di profilazione.
                Puoi accettarli, rifiutarli o personalizzarli. Maggiori dettagli nella{" "}
                <Link to="/cookie-policy" className="underline hover:text-white" data-testid="banner-cookie-link">Cookie Policy</Link>.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
              <button data-testid="cookie-reject-all" onClick={rejectAll} className="text-xs uppercase tracking-wide border border-white/30 px-5 py-3 hover:bg-white/10 transition-colors">
                Rifiuta tutto
              </button>
              <button data-testid="cookie-manage" onClick={() => setShowPrefs(true)} className="text-xs uppercase tracking-wide border border-white/30 px-5 py-3 hover:bg-white/10 transition-colors">
                Gestisci preferenze
              </button>
              <button data-testid="cookie-accept-all" onClick={acceptAll} className="text-xs uppercase tracking-wide bg-white text-black px-5 py-3 hover:bg-white/90 transition-colors">
                Accetta tutto
              </button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={showPrefs} onOpenChange={(o) => { if (!o) { setShowPrefs(false); if (!getCookieConsent()) setVisible(true); } }}>
        <DialogContent className="max-w-lg" data-testid="cookie-preferences-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light">Preferenze cookie</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <Pref title="Cookie necessari" desc="Indispensabili per il funzionamento del sito. Sempre attivi." checked disabled />
            <Pref title="Cookie analitici" desc="Ci aiutano a capire come viene utilizzato il sito (in forma aggregata)." checked={analytics} onChange={setAnalytics} testid="pref-analytics" />
            <Pref title="Cookie di profilazione / marketing" desc="Usati per mostrarti contenuti e annunci personalizzati." checked={marketing} onChange={setMarketing} testid="pref-marketing" />
          </div>
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button data-testid="cookie-reject-all-dialog" onClick={rejectAll} className="flex-1 text-xs uppercase tracking-wide border border-black/20 px-5 py-3 hover:bg-black/5 transition-colors">
              Rifiuta tutto
            </button>
            <button data-testid="cookie-save-prefs" onClick={savePrefs} className="flex-1 text-xs uppercase tracking-wide bg-black text-white px-5 py-3 hover:bg-black/80 transition-colors">
              Salva preferenze
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const Pref = ({ title, desc, checked, onChange, disabled, testid }) => (
  <div className="flex items-start justify-between gap-4 border-b border-black/10 pb-5">
    <div className="flex-1">
      <p className="font-medium text-sm">{title}</p>
      <p className="text-xs text-muted-foreground font-light mt-1 text-left">{desc}</p>
    </div>
    <input
      type="checkbox"
      data-testid={testid}
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange && onChange(e.target.checked)}
      className="mt-1 h-5 w-5 shrink-0 accent-champagne disabled:opacity-50 cursor-pointer"
    />
  </div>
);
